import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Calendar,
  Package,

  AlertCircle,
  Loader2,
  ShoppingCart,
  Users,
  Plus,
  Eye,
} from "lucide-react";
import { orderService, familyService, type BackendOrder, type Student } from "@/services";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/hooks/useAuth";
import { AssignTicketsModal } from "@/components/business/family/ChildrenManagement/AssignTicketsModal";

/**
 * Componente para mostrar el historial de compras con funcionalidad de asignación
 */
export function OrderHistoryWithAssignments() {
  const [orders, setOrders] = useState<BackendOrder[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [_, setIsLoadingStudents] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<BackendOrder | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  /**
   * Carga las órdenes y estudiantes al montar el componente
   */
  useEffect(() => {
    loadOrders();
    loadStudents();
  }, []);

  /**
   * Carga las órdenes desde el backend
   */
  const loadOrders = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Preferimos cargar desde transacciones del usuario autenticado
      // para soportar el nuevo endpoint /api/v1/transactions/mine
      let ordersData: BackendOrder[] = [];
      try {
        ordersData = await orderService.getMyOrdersFromTransactions();
      } catch (e) {
        // fallback al endpoint clásico si no está disponible
        ordersData = await orderService.getBackendOrders();
      }
      setOrders(ordersData);
    } catch (err: any) {
      console.error("Error cargando órdenes:", err);
      setError(err.message || "Error al cargar las órdenes");
      toast({
        title: "Error",
        description: "No se pudieron cargar las órdenes",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Carga los estudiantes del apoderado
   */
  const loadStudents = async () => {
    if (!user?.id) return;

    try {
      setIsLoadingStudents(true);
      const studentsData = await familyService.getLinkedStudents();
      setStudents(studentsData.linkedStudents);
    } catch (error: any) {
      console.error("Error cargando estudiantes:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los estudiantes",
        variant: "destructive",
      });
    } finally {
      setIsLoadingStudents(false);
    }
  };

  /**
   * Formatea el precio como moneda chilena
   */
  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  /**
   * Formatea la fecha en formato chileno
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /**
   * Obtiene el estado y color del badge según el estado de la orden
   * FUNCIÓN COMENTADA - Ya no se usa la columna Estado
   */
  /*
  const getOrderStatus = (estado: string) => {
    switch (estado.toUpperCase()) {
      case "PAGADO":
        return {
          label: "Pagado",
          variant: "default" as const,
          className: "bg-green-100 text-green-800",
          icon: CheckCircle,
        };
      case "PENDIENTE":
        return {
          label: "Pendiente",
          variant: "secondary" as const,
          className: "bg-yellow-100 text-yellow-800",
          icon: Clock,
        };
      case "CANCELADO":
        return {
          label: "Cancelado",
          variant: "destructive" as const,
          className: "bg-red-100 text-red-800",
          icon: AlertCircle,
        };
      default:
        return {
          label: estado,
          variant: "outline" as const,
          className: "bg-gray-100 text-gray-800",
          icon: Package,
        };
    }
  };
  */

  /**
   * Maneja la visualización del detalle de la orden
   */
  const handleViewOrderDetail = (orderId: string) => {
    navigate(`/dashboard/orders/${orderId}?context=purchase-history`);
  };

  /**
   * Maneja la asignación de productos desde una orden
   */
  const handleAssignFromOrder = (order: BackendOrder) => {
    setSelectedOrder(order);
    setIsAssignModalOpen(true);
  };

  /**
   * Maneja el éxito de la asignación
   */
  const handleAssignSuccess = () => {
    setIsAssignModalOpen(false);
    setSelectedOrder(null);
    setSelectedStudent(null);
    toast({
      title: "Éxito",
      description: "Productos asignados correctamente",
    });
    // Recargar estudiantes para actualizar saldos
    loadStudents();
  };

  // Estado de carga
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">
            Cargando historial de compras...
          </p>
        </div>
      </div>
    );
  }

  // Estado de error
  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-red-700 mb-2">
          Error al cargar órdenes
        </h3>
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={loadOrders} variant="outline">
          Intentar de nuevo
        </Button>
      </div>
    );
  }

  // Sin órdenes
  if (orders.length === 0) {
    return (
      <div className="text-center py-8">
        <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground mb-2">
          No hay compras realizadas
        </h3>
        <p className="text-muted-foreground mb-4">
          Realiza tu primera compra para poder asignar productos a tus hijos
        </p>
        <Button onClick={() => navigate("/store")} className="h-12 text-base">
          <ShoppingCart className="h-4 w-4 mr-2" />
          Ir a la Tienda
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Información de estudiantes */}
      {students.length > 0 && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">
                Hijos registrados: {students.length}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Puedes asignar productos de tus compras a tus hijos desde cada
              orden
            </p>
          </CardContent>
        </Card>
      )}

      {/* Vista Desktop - Tabla */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow key="orders-header-row">
              <TableHead>ID de Orden</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Productos</TableHead>
              <TableHead>Total</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => {
              return (
                <TableRow key={order.id}>
                  <TableCell className="font-mono text-sm">
                    {order.id.slice(0, 8)}...
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {formatDate(order.createdAt)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span>{order.itemsCount} productos</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">
                    {formatPrice(order.total)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => handleViewOrderDetail(order.id)}
                        size="sm"
                        variant="outline"
                        className="h-8"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Ver
                      </Button>
                      {students.length > 0 && (
                        <Button
                          onClick={() => handleAssignFromOrder(order)}
                          size="sm"
                          className="h-8"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Asignar
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Vista Mobile - Cards */}
      <div className="md:hidden space-y-4">
        {orders.map((order) => {
          return (
            <Card key={order.id} className="hover:shadow-sm transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span className="font-mono text-sm">
                      {order.id.slice(0, 8)}...
                    </span>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(order.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Package className="h-4 w-4" />
                    <span>{order.itemsCount} productos</span>
                  </div>
                  <div className="text-lg font-semibold">
                    {formatPrice(order.total)}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleViewOrderDetail(order.id)}
                    variant="outline"
                    size="sm"
                    className="flex-1 h-10"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Ver Detalle
                  </Button>
                  {students.length > 0 && (
                    <Button
                      onClick={() => handleAssignFromOrder(order)}
                      size="sm"
                      className="flex-1 h-10"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Asignar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Modal de asignación */}
      {selectedOrder && (
        <AssignTicketsModal
          isOpen={isAssignModalOpen}
          onClose={() => {
            setIsAssignModalOpen(false);
            setSelectedOrder(null);
          }}
          student={selectedStudent}
          apoderadoId={user?.id || ""}
          onSuccess={handleAssignSuccess}
          orderId={selectedOrder.id}
        />
      )}
    </div>
  );
}
